import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import {
  ContentSection,
  AutoTOC,
  KeyPointsBlock,
  Sources,
  Warn,
  PL,
  Sub,
} from "../components/contentTemplate";

const SECTIONS = [
  { id: "qui-peut",     title: "Qui peut travailler en Pologne ?" },
  { id: "salaires",     title: "Les salaires en Pologne : les vrais chiffres" },
  { id: "contrats",     title: "Les quatre types de contrats" },
  { id: "brut-net",     title: "Du brut au net : comprendre sa fiche de paie" },
  { id: "francophones", title: "Qui recrute des francophones" },
  { id: "droits",       title: "Vos droits de salarié : l'essentiel du Kodeks pracy" },
  { id: "chercher",     title: "Trouver un emploi : où et comment chercher" },
  { id: "faq",          title: "FAQ" },
];

const INTERNAL = "text-[#0056D2] hover:underline";
const EXTERNAL = "text-[#0056D2] hover:underline";

const KODEKS_PRACY_URL =
  "https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=WDU19740240141";

const SOURCES = [
  {
    url: "https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=WDU20250001242",
    label:
      "Rozporządzenie Rady Ministrów z dnia 11 września 2025 r. w sprawie wysokości minimalnego wynagrodzenia za pracę oraz wysokości minimalnej stawki godzinowej w 2026 r. (Dz.U. 2025 poz. 1242)",
    description:
      "Établit le salaire minimum 2026 (4 806 zł) et le taux horaire minimum (31,40 zł).",
  },
  {
    url: KODEKS_PRACY_URL,
    label:
      "Ustawa z dnia 26 czerwca 1974 r. Kodeks pracy, tekst ujednolicony (fiche ISAP)",
    description:
      "Établit les règles de congés, préavis, période d'essai et protection du salarié.",
  },
  {
    url: "https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=WDU20022001679",
    label:
      "Ustawa z dnia 10 października 2002 r. o minimalnym wynagrodzeniu za pracę, tekst ujednolicony (fiche ISAP)",
    description:
      "Établit le mécanisme légal de fixation du salaire minimum.",
  },
  {
    url: "https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=WDU20250001423",
    label:
      "Ustawa z dnia 26 września 2025 r. o zmianie ustawy - Kodeks pracy oraz niektórych innych ustaw (Dz.U. 2025 poz. 1423)",
    description:
      "Établit la prise en compte des périodes d'umowa zlecenie, d'activité indépendante et de travail à l'étranger dans le staż pracy (secteur public au 1er janvier 2026, autres employeurs au 1er mai 2026).",
  },
  {
    url: "https://stat.gov.pl",
    label:
      "GUS, communiqués sur le salaire moyen dans le secteur des entreprises",
    description:
      "Établit le salaire moyen de 9 173,24 zł en mai 2026 et sa méthodologie (entités de 10 salariés et plus).",
  },
  {
    url: "https://absl.pl/en/report/business-services-sector-poland-2025",
    label: "ABSL, Business Services Sector in Poland 2025",
    description:
      "Établit l'emploi du secteur (488 700 personnes fin T1 2025), la part d'étrangers et le poids des villes.",
  },
  {
    url: "https://www.zus.pl",
    label: "ZUS",
    description:
      "Établit les taux de cotisations sociales à la charge du salarié.",
  },
  {
    url: "https://ec.europa.eu/social",
    label: "Commission européenne, coordination de la sécurité sociale",
    description:
      "Établit les règles d'exportation du chômage (formulaire U2) et de totalisation des périodes pour la retraite.",
  },
  {
    url: "https://www.pip.gov.pl",
    label: "Państwowa Inspekcja Pracy",
    description: "Référence pratique pour les droits du salarié.",
  },
];

export default function TravaillerPologne() {
  const { lp } = useT();

  return (
    <article
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
      data-testid="travailler-pologne-page"
    >
      <header className="mb-10" data-testid="page-hero">
        <span className="hej-chip" data-testid="page-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          Mis à jour en juillet 2026
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          Travailler en Pologne
        </h1>
      </header>

      <section className="space-y-4 text-slate-700 leading-relaxed text-lg mb-12" data-testid="page-lede">
        <p>
          Combien gagne-t-on réellement en Pologne, et que reste-t-il une fois le loyer payé ? C'est la première question de
          quiconque envisage d'y travailler, et c'est celle à laquelle cette page répond avec des chiffres officiels plutôt
          que des impressions.
        </p>
        <p>
          Vous y trouverez les salaires 2026 sourcés au texte de loi près, le fonctionnement des quatre types de contrats
          polonais, le passage du brut au net et les secteurs qui recrutent des francophones. Pour la couverture maladie liée
          à votre contrat, voyez notre page{" "}
          <Link to={lp("/sante")} className={INTERNAL}>Se soigner en Pologne</Link>, et pour mettre ces salaires en face des
          dépenses réelles, notre page{" "}
          <Link to={lp("/cout-de-la-vie")} className={INTERNAL}>Coût de la vie</Link>.
        </p>
      </section>

      <KeyPointsBlock testid="page-key-points">
        <ul className="list-disc pl-5 space-y-2">
          <li>Salaire minimum 2026 : 4 806 zł brut (~3 606 zł net), sans revalorisation en juillet.</li>
          <li>Citoyens UE, EEE et Suisse : aucun permis de travail nécessaire.</li>
          <li>Le type de contrat (umowa o pracę, zlecenie, dzieło, B2B) détermine vos protections.</li>
          <li>Le net représente 72 à 75 % du brut pour les salaires courants.</li>
        </ul>
      </KeyPointsBlock>

      <AutoTOC sections={SECTIONS} testid="page-toc" />

      <ContentSection id="qui-peut" n={1} title="Qui peut travailler en Pologne ?">
        <p>
          Si vous êtes citoyen français, belge, d'un autre pays de l'UE ou de l'EEE, ou ressortissant suisse, vous n'avez
          besoin d'aucun permis de travail: vous pouvez signer un contrat dès votre arrivée. Seule formalité,
          l'enregistrement de votre séjour auprès du bureau de voïvodie si vous restez plus de trois mois, une démarche que
          notre page{" "}
          <Link to={lp("/demarches")} className={INTERNAL}>Démarches</Link> détaille pas à pas, PESEL compris. Les
          ressortissants de pays tiers suivent un tout autre parcours: l'emploi passe en général par un permis unique de
          séjour et de travail demandé avec le concours de l'employeur, ou par la Carte bleue européenne pour les profils
          hautement qualifiés, avec des délais et un dossier qui n'ont rien de commun avec la libre circulation. Notre page{" "}
          <Link to={lp("/demarches-pays-tiers")} className={INTERNAL}>Démarches pays tiers</Link> leur est consacrée. Le
          reste de cette page vaut pour tous: les salaires, les contrats et les droits décrits ci-dessous ne dépendent pas
          de la nationalité.
        </p>
      </ContentSection>

      <ContentSection id="salaires" n={2} title="Les salaires en Pologne : les vrais chiffres">
        <p>
          Le salaire minimum polonais (<PL>płaca minimalna</PL>) est fixé à 4 806 zł brut par mois pour toute l'année 2026,
          soit environ 3 606 zł net pour un salarié standard à temps plein. Le montant est établi par le règlement du
          Conseil des ministres du 11 septembre 2025{" "}
          <a
            href="https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=WDU20250001242"
            target="_blank"
            rel="noopener noreferrer"
            className={EXTERNAL}
          >
            (Dz.U. 2025 poz. 1242)
          </a>
          . Contrairement à 2023 et 2024, il n'y a pas de seconde revalorisation en juillet : le montant vaut du 1er janvier
          au 31 décembre. Pour les contrats civils (<PL>umowa zlecenie</PL> et prestations de services), c'est un taux
          horaire minimum qui s'applique, 31,40 zł brut de l'heure en 2026.
        </p>
        <p>
          Le salaire moyen raconte une autre histoire. Selon le GUS, l'office statistique polonais, le salaire moyen brut
          dans le secteur des entreprises atteignait 9 173 zł en mai 2026{" "}
          <a href="https://stat.gov.pl" target="_blank" rel="noopener noreferrer" className={EXTERNAL}>
            (stat.gov.pl)
          </a>
          , en hausse de 5,8 % sur un an. Ce chiffre demande deux précautions de lecture. D'abord, il ne couvre que les
          entités de dix salariés et plus : les microentreprises et une partie de la fonction publique en sont absentes.
          Ensuite, c'est une moyenne tirée vers le haut par les hauts salaires de l'informatique, de la finance et de
          l'énergie ; la médiane, publiée moins fréquemment par le GUS, lui est nettement inférieure. Un salaire de 6 000 à
          7 000 zł brut place déjà au-dessus de ce que gagne une majorité de salariés polonais.
        </p>

        <div
          className="hej-card p-6 md:p-8 my-6 bg-slate-50 border border-slate-200"
          data-testid="key-figures-card"
        >
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">
            Chiffres clés
          </h3>
          <div className="space-y-2 text-slate-700 leading-relaxed">
            <p><strong>Salaire minimum 2026 :</strong> 4 806 zł brut / ~3 606 zł net</p>
            <p><strong>Taux horaire minimum (contrats civils) :</strong> 31,40 zł brut</p>
            <p><strong>Salaire moyen secteur entreprises (mai 2026, GUS) :</strong> 9 173 zł brut</p>
            <p><strong>Équivalents indicatifs :</strong> 4 806 zł ≈ 1 120 € ≈ 1 030 CHF ; 9 173 zł ≈ 2 140 € ≈ 1 965 CHF (taux moyens NBP du 2 juillet 2026 : 1 € = 4,29 zł, 1 CHF = 4,67 zł)</p>
          </div>
        </div>

        <p>
          Pour juger de ce que valent ces montants au quotidien, loyers, courses et transports compris, notre page{" "}
          <Link to={lp("/cout-de-la-vie")} className={INTERNAL}>Coût de la vie en Pologne</Link> donne les prix réels ville
          par ville.
        </p>
      </ContentSection>

      <ContentSection id="contrats" n={3} title="Les quatre types de contrats">
        <p>
          Le droit polonais connaît quatre grandes formes de collaboration, et la différence entre elles pèse lourd sur
          votre protection sociale.
        </p>
        <p>
          L'<PL>umowa o pracę</PL> est le contrat de travail au sens plein, l'équivalent de votre CDI ou CDD. Il est régi
          par le Code du travail (<PL>Kodeks pracy</PL>,{" "}
          <a href={KODEKS_PRACY_URL} target="_blank" rel="noopener noreferrer" className={EXTERNAL}>
            fiche ISAP, tekst ujednolicony
          </a>
          ) et emporte tout le paquet : congés payés, préavis, protection contre le licenciement, cotisations complètes et
          affiliation automatique à l'assurance maladie publique NFZ. C'est la forme que privilégient les grands employeurs
          et la seule qui offre la sécurité comparable à ce que connaissent les salariés français, belges ou suisses.
        </p>
        <p>
          L'<PL>umowa zlecenie</PL> (contrat de mandat) est un contrat civil : la protection du Code du travail ne
          s'applique pas, il n'y a ni congés payés légaux ni préavis garanti, mais les cotisations sociales et l'assurance
          maladie sont dues dans la plupart des cas. Le taux horaire minimum de 31,40 zł s'y applique. C'est un format
          courant pour les emplois étudiants et les missions courtes.
        </p>
        <p>
          L'<PL>umowa o dzieło</PL> (contrat d'ouvrage) rémunère un résultat précis, une traduction ou un site web livré
          par exemple. C'est le seul contrat qui échappe à la fois au salaire minimum et, sauf exceptions, aux cotisations
          sociales : aucune couverture maladie n'en découle. Méfiance si un employeur vous le propose pour un travail
          régulier.
        </p>
        <p>
          Le B2B consiste à facturer votre employeur comme entrepreneur individuel. Les salaires bruts affichés sont plus
          élevés, mais vous portez vous-même vos cotisations ZUS, votre comptabilité et votre absence de congés payés.
          Notre page{" "}
          <Link to={lp("/entrepreneuriat")} className={INTERNAL}>Entrepreneuriat</Link> détaille ce régime, ses cotisations
          et ses pièges.
        </p>

        <Warn testid="warn-b2b">
          Certains employeurs, notamment dans l'informatique, imposent le B2B ou l'<PL>umowa zlecenie</PL> pour un poste qui
          a toutes les caractéristiques d'un emploi salarié (horaires imposés, subordination, lieu de travail fixe). Le
          droit polonais qualifie cette pratique de contournement du contrat de travail. Avant d'accepter, comparez ce que
          vous perdez en protection avec ce que vous gagnez en brut.
        </Warn>
      </ContentSection>

      <ContentSection id="brut-net" n={4} title="Du brut au net : comprendre sa fiche de paie">
        <p>
          Sur une <PL>umowa o pracę</PL>, trois prélèvements séparent le brut du net. Les cotisations sociales ZUS à la
          charge du salarié représentent 13,71 % du brut (retraite 9,76 %, invalidité 1,5 %, maladie 2,45 %). La cotisation
          d'assurance santé prélève ensuite 9 % de la base restante. Vient enfin l'acompte d'impôt sur le revenu (PIT), au
          taux de 12 % jusqu'à 120 000 zł de revenus annuels puis de 32 % au-delà, après un abattement de 30 000 zł par an
          qui ne génère aucun impôt.
        </p>
        <p>
          Concrètement, le salarié au minimum de 4 806 zł brut touche environ 3 606 zł net. Un salaire de 9 000 zł brut,
          proche de la moyenne du secteur des entreprises, laisse environ 6 465 zł net. L'ordre de grandeur à retenir : le
          net représente 72 à 75 % du brut pour les salaires courants.
        </p>
        <p>
          Un avantage mérite d'être connu des jeunes candidats au départ : les salariés de moins de 26 ans sont exonérés
          d'impôt sur le revenu jusqu'à 85 528 zł de revenus annuels (<PL>ulga dla młodych</PL>). Les cotisations sociales
          restent dues, mais le net s'en trouve sensiblement relevé.
        </p>
      </ContentSection>

      <ContentSection id="francophones" n={5} title="Qui recrute des francophones">
        <p>
          Le premier employeur de francophones en Pologne est le secteur des services aux entreprises, ces centres (SSC,
          BPO, GBS) où les multinationales regroupent leur comptabilité, leur support client, leurs achats ou leur
          informatique pour toute l'Europe. Selon le rapport ABSL 2025{" "}
          <a
            href="https://absl.pl/en/report/business-services-sector-poland-2025"
            target="_blank"
            rel="noopener noreferrer"
            className={EXTERNAL}
          >
            (absl.pl)
          </a>
          , le secteur employait près de 489 000 personnes fin du premier trimestre 2025, dont environ 20 % d'étrangers, et
          pesait 5,7 % du PIB polonais. Varsovie et Cracovie dépassent chacune 100 000 emplois dans ces centres, Wrocław et
          la Tricité (Gdańsk, Gdynia, Sopot) suivent. Le français y est une compétence recherchée et rémunérée : les postes
          de comptabilité fournisseurs, de support client ou d'administration RH « avec français » s'adressent précisément
          aux candidats qui ne parlent pas polonais. Fait parlant, la Suisse figure parmi les cinq premiers pays clients
          des services exportés depuis la Pologne.
        </p>
        <p>
          Les autres viviers sont l'enseignement du français (Alliance française, écoles de langues, lycées
          internationaux), les entreprises françaises implantées en Pologne (la CCI France Pologne recense plusieurs
          centaines de membres, de Decathlon à Orange en passant par le Crédit Agricole) et, pour les profils techniques,
          l'informatique où l'anglais suffit presque partout.
        </p>
        <p>
          Un mot de réalisme sur les salaires d'entrée : un poste junior « avec français » dans un centre de services se
          négocie généralement entre 6 000 et 8 500 zł brut selon la ville et la spécialité, davantage avec de l'expérience
          ou une seconde langue rare. C'est confortable au regard du coût de la vie polonais, mais ce n'est pas un salaire
          suisse transposé à Cracovie.
        </p>
      </ContentSection>

      <ContentSection id="droits" n={6} title="Vos droits de salarié : l'essentiel du Kodeks pracy">
        <p>
          Le Code du travail polonais garantit au salarié en <PL>umowa o pracę</PL> 20 jours de congés payés par an,
          portés à 26 jours à partir de dix ans d'ancienneté totale, sachant que les années d'études comptent dans ce
          calcul (huit ans pour un master). S'y ajoutent quatorze jours fériés, la veille de Noël étant fériée depuis 2025.
        </p>
        <p>
          Une réforme récente change la donne pour beaucoup de profils: depuis 2026, l'ancienneté (<PL>staż pracy</PL>) ne
          se limite plus aux années passées en contrat de travail. La loi du 26 septembre 2025{" "}
          <a
            href="https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=WDU20250001423"
            target="_blank"
            rel="noopener noreferrer"
            className={EXTERNAL}
          >
            (Dz.U. 2025 poz. 1423)
          </a>{" "}
          fait compter les périodes d'<PL>umowa zlecenie</PL> ayant donné lieu à cotisations, d'activité indépendante et
          même de travail salarié documenté à l'étranger, y compris les périodes antérieures à la réforme. La règle
          s'applique depuis le 1er janvier 2026 au secteur public et depuis le 1er mai 2026 à tous les autres employeurs.
          Concrètement, vos années travaillées en France, en Belgique ou en Suisse peuvent faire franchir le seuil des dix
          ans et ouvrir droit aux 26 jours de congés dès l'embauche: il faut les documenter auprès de l'employeur
          (attestation ZUS ou justificatifs étrangers) dans un délai de 24 mois. L'<PL>umowa o dzieło</PL> reste exclue du
          dispositif.
        </p>
        <p>
          Le préavis de licenciement comme de démission dépend de l'ancienneté chez l'employeur : deux semaines en dessous
          de six mois, un mois entre six mois et trois ans, trois mois au-delà. La période d'essai est plafonnée à trois
          mois et, depuis la réforme de 2023, sa durée maximale dépend de la durée du contrat envisagé ensuite. Le
          licenciement d'un CDI doit être motivé par écrit, et certaines catégories (femmes enceintes, salariés à moins de
          quatre ans de la retraite) bénéficient d'une protection renforcée.
        </p>
        <p>
          Cette section donne les repères, pas le détail. Pour un litige ou une situation particulière, l'Inspection
          nationale du travail (<PL>Państwowa Inspekcja Pracy</PL>) publie des fiches et répond aux questions, y compris en
          anglais. En cas de salaire impayé ou de licenciement contesté, c'est le tribunal du travail
          (<PL>sąd pracy</PL>) qui tranche, avec une procédure gratuite pour les créances jusqu'à 50 000 zł et un délai de
          prescription de trois ans pour les créances salariales.
        </p>
      </ContentSection>

      <ContentSection id="chercher" n={7} title="Trouver un emploi : où et comment chercher">
        <p>
          Les recherches se font pour l'essentiel sur trois canaux : pracuj.pl, le portail généraliste dominant, LinkedIn,
          très utilisé par les centres de services et les multinationales, et justjoin.it ou nofluffjobs.com pour la tech.
          Cherchez avec les mots-clés « french » ou « język francuski » : c'est ainsi que sont étiquetées les offres qui
          vous concernent.
        </p>
        <p>
          Le CV polonais ressemble au CV français, une page ou deux, photo facultative, avec une particularité : une
          clause de consentement RGPD au traitement des données, que les employeurs exigent (les offres en fournissent la
          formule). Les diplômes des pays de l'UE sont reconnus sans formalité pour la plupart des emplois privés ; seules
          les professions réglementées (santé, droit, architecture) demandent une reconnaissance formelle.
        </p>
        <p>
          Une fois le contrat signé, votre employeur vous déclare au ZUS sous sept jours, ce qui ouvre vos droits NFZ
          (<Link to={lp("/sante")} className={INTERNAL}>Se soigner en Pologne</Link>). Restent vos propres démarches
          d'enregistrement de séjour, différentes selon que vous êtes citoyen de l'UE, de l'EEE ou de Suisse, ou
          ressortissant d'un pays tiers : elles sont détaillées sur nos pages{" "}
          <Link to={lp("/demarches")} className={INTERNAL}>Démarches</Link> et{" "}
          <Link to={lp("/demarches-pays-tiers")} className={INTERNAL}>Démarches pays tiers</Link>. Et pour trouver un toit
          avant le premier jour de travail, notre page{" "}
          <Link to={lp("/logement")} className={INTERNAL}>Logement</Link> couvre la recherche d'appartement et les
          particularités des baux polonais.
        </p>
      </ContentSection>

      <ContentSection id="faq" n={8} title="FAQ">
        <Sub>Peut-on travailler en Pologne sans parler polonais ?</Sub>
        <p>
          Oui, et pas seulement dans des cas marginaux. Les centres de services aux entreprises, qui emploient près de
          489 000 personnes selon le rapport ABSL 2025, recrutent sur les langues : un poste « comptabilité avec
          français » à Cracovie, Varsovie ou Wrocław se travaille en français et en anglais, le polonais n'étant pas
          requis. L'informatique fonctionne largement en anglais. En dehors de ces secteurs internationaux, en revanche,
          le polonais reste indispensable, et il le demeure pour la vie administrative quotidienne.
        </p>

        <Sub>Quel est le salaire minimum en Pologne en 2026 ?</Sub>
        <p>
          4 806 zł brut par mois, soit environ 3 606 zł net, fixé par le règlement du Conseil des ministres du 11
          septembre 2025 (Dz.U. 2025 poz. 1242). Le taux horaire minimum des contrats civils est de 31,40 zł brut. Ces
          montants valent pour toute l'année 2026, sans revalorisation en juillet.
        </p>

        <Sub>Mon salaire polonais est-il imposé en France, en Belgique ou en Suisse ?</Sub>
        <p>
          Le principe des conventions fiscales est que le salaire d'un emploi exercé en Pologne y est imposé. Deux clés de
          lecture aident à se situer: votre résidence fiscale, déterminée par le foyer permanent et le centre de vos
          intérêts vitaux, et la règle dite des 183 jours, qui régit les missions courtes exercées dans un pays pour un
          employeur d'un autre. Les situations mixtes (télétravail partiel depuis la France, famille restée en Belgique,
          statut de frontalier) créent des cas particuliers que seul un examen individuel tranche. Pour la logique des
          conventions bilatérales, notre page{" "}
          <Link to={lp("/retraite-pologne")} className={INTERNAL}>Retraite en Pologne</Link> en détaille le fonctionnement
          pour les pensions.
        </p>

        <Sub>Puis-je transférer mes allocations chômage en Pologne ?</Sub>
        <p>
          Oui, temporairement. Le formulaire U2 permet d'exporter ses allocations françaises ou belges pendant trois mois
          (prolongeables jusqu'à six selon les cas) pour chercher un emploi en Pologne, à condition d'en faire la demande
          avant le départ et de s'inscrire auprès du service polonais de l'emploi dans les sept jours. La Suisse participe
          à ce mécanisme via l'accord sur la libre circulation. Passé ce délai sans emploi, les droits cessent d'être
          versés.
        </p>

        <Sub>Mes années travaillées en Pologne compteront-elles pour ma retraite ?</Sub>
        <p>
          Oui. Les règlements européens de coordination, que la Suisse applique également, totalisent les périodes
          cotisées dans chaque pays pour l'ouverture des droits, et chaque pays verse ensuite sa part de pension au prorata
          des années cotisées chez lui. Rien n'est perdu, mais rien n'est transféré non plus : vous percevrez un jour une
          pension polonaise pour vos années polonaises. Notre page{" "}
          <Link to={lp("/retraite-pologne")} className={INTERNAL}>Retraite en Pologne</Link> explique le mécanisme.
        </p>

        <Sub>Combien reste-t-il net d'un salaire brut polonais ?</Sub>
        <p>
          Entre 72 et 75 % pour les salaires courants sur un contrat de travail : environ 3 606 zł net pour le minimum de
          4 806 zł brut, environ 6 465 zł net pour 9 000 zł brut. Les moins de 26 ans, exonérés d'impôt sur le revenu,
          touchent davantage. Le détail des prélèvements figure dans la section « Du brut au net » ci-dessus.
        </p>
      </ContentSection>

      <Sources sources={SOURCES} testid="page-sources" />
    </article>
  );
}
